import React from "react";
import { Buildings, FolderSimple, Trash } from "@phosphor-icons/react";
import { ROLE_LABELS, ROLE_DESCRIPTIONS, GLOBAL_ROLES, CLIENT_ROLES } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { SearchableSelect } from "./ui/searchable-select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

function AssignmentEditor({ value, onChange, orgs, projectsByOrg, singleClient }) {
  const used = new Set(value.map((a) => a.organization_id));
  const available = orgs.filter((o) => !used.has(o.id));
  const atCap = singleClient && value.length >= 1;
  const addClient = (orgId) => onChange([...value, { organization_id: orgId, all_projects: true, project_ids: [] }]);
  const removeClient = (orgId) => onChange(value.filter((a) => a.organization_id !== orgId));
  const patch = (orgId, fields) => onChange(value.map((a) => (a.organization_id === orgId ? { ...a, ...fields } : a)));
  const toggleProject = (orgId, projId) => {
    const a = value.find((x) => x.organization_id === orgId);
    const ids = a.project_ids.includes(projId) ? a.project_ids.filter((p) => p !== projId) : [...a.project_ids, projId];
    patch(orgId, { project_ids: ids });
  };
  return (
    <div className="space-y-2.5" data-testid="assignment-editor">
      {value.length === 0 && (
        <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md py-3 text-center">
          {singleClient
            ? "Assign the client this user belongs to."
            : "No client assigned yet — they'll only see tickets assigned directly to them."}
        </div>
      )}
      {value.map((a) => {
        const org = orgs.find((o) => o.id === a.organization_id);
        const projects = projectsByOrg[a.organization_id] || [];
        return (
          <div key={a.organization_id} className="border border-border rounded-md p-3 space-y-2.5 bg-slate-50/60">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-medium text-slate-800 text-sm">
                <Buildings size={15} className="text-blue-600" />
                {org ? org.name : "Unknown client"}
              </div>
              <button type="button" onClick={() => removeClient(a.organization_id)} className="text-slate-400 hover:text-rose-600" title="Remove client">
                <Trash size={14} />
              </button>
            </div>
            <div className="flex items-center gap-2.5">
              <Switch checked={a.all_projects}
                onCheckedChange={(v) => patch(a.organization_id, { all_projects: v, project_ids: v ? [] : a.project_ids })} />
              <span className="text-xs text-slate-600">{a.all_projects ? "All projects of this client" : "Specific projects only"}</span>
            </div>
            {!a.all_projects && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                    <FolderSimple size={13} className="mr-1.5" />
                    {a.project_ids.length ? `${a.project_ids.length} project(s) selected` : "Select projects"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  {projects.length === 0 && <div className="text-xs text-slate-400 p-2">No projects for this client.</div>}
                  <div className="max-h-52 overflow-auto space-y-0.5">
                    {projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                        <Checkbox checked={a.project_ids.includes(p.id)} onCheckedChange={() => toggleProject(a.organization_id, p.id)} />
                        <span className="text-slate-700">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        );
      })}
      {available.length > 0 && !atCap && (
        <SearchableSelect
          value=""
          onValueChange={addClient}
          options={available.map((o) => ({ value: o.id, label: o.name }))}
          placeholder="+ Assign a client…"
          className="h-8 text-xs"
          testId="add-client-select"
        />
      )}
      {atCap && value.length > 1 && (
        <div className="text-xs text-amber-600">Client roles can belong to only one client — remove the extra ones.</div>
      )}
    </div>
  );
}

const Help = ({ children }) => <p className="text-[11px] text-slate-400 mt-1">{children}</p>;

export default function UserForm({ mode, value, onChange, roleOptions, orgs, projectsByOrg, onSubmit, submitting, error, onCancel, userId, onResetPassword }) {
  const isEdit = mode === "edit";
  const isGlobalRole = GLOBAL_ROLES.includes(value.role);
  const set = (patch) => onChange({ ...value, ...patch });
  const desc = ROLE_DESCRIPTIONS[value.role];

  const [resetState, setResetState] = React.useState("idle"); // idle | sending | sent | error
  const doReset = async () => {
    setResetState("sending");
    try {
      const res = await onResetPassword(userId);
      setResetState(res && res.email_sent === false ? "nomail" : "sent");
    } catch {
      setResetState("error");
    }
  };

  return (
    <form onSubmit={onSubmit} className="surface-card p-6 space-y-5" data-testid="user-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Name</Label>
          <Input required value={value.name} onChange={(e) => set({ name: e.target.value })} data-testid="user-name-input" />
          <Help>The person's full name, shown on tickets and comments.</Help>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Email</Label>
          <Input required type="email" disabled={isEdit} value={value.email} onChange={(e) => set({ email: e.target.value })} data-testid="user-email-input" />
          <Help>{isEdit ? "Email can't be changed after the account is created." : "They'll sign in with this address."}</Help>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isEdit && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Password</Label>
            <Button type="button" variant="outline" onClick={doReset} disabled={resetState === "sending"}
              className="w-full justify-center border-slate-300" data-testid="user-reset-password-button">
              {resetState === "sending" ? "Sending…" : "Send password reset link"}
            </Button>
            {resetState === "sent" && <Help>Reset link sent to {value.email}.</Help>}
            {resetState === "nomail" && <Help>Email isn't configured — no message was sent.</Help>}
            {resetState === "error" && <Help>Couldn't send the reset link. Try again.</Help>}
            {resetState === "idle" && <Help>Emails the user a secure link to choose a new password. You never see or set it.</Help>}
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Role</Label>
          <SearchableSelect
            value={value.role}
            onValueChange={(v) => set({ role: v })}
            options={roleOptions.map((k) => ({ value: k, label: ROLE_LABELS[k] }))}
            testId="user-role-select"
          />
          {desc && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2 mt-1">
              <div>{desc.blurb}</div>
              <div className="text-slate-400 mt-0.5"><span className="font-medium text-slate-500">Access:</span> {desc.access}</div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Client &amp; project access</Label>
        {isGlobalRole ? (
          <div className="text-xs text-slate-500 bg-blue-50/60 border border-blue-100 rounded-md px-3 py-2">
            {ROLE_LABELS[value.role]}s have full access to every client and project.
          </div>
        ) : (
          <AssignmentEditor value={value.assignments} orgs={orgs} projectsByOrg={projectsByOrg}
            singleClient={CLIENT_ROLES.includes(value.role)}
            onChange={(assignments) => set({ assignments })} />
        )}
        <Help>Which clients and projects this user can see. Global roles see everything.</Help>
      </div>

      {isEdit && (
        <div className="flex items-center gap-2.5 pt-1">
          <Switch checked={value.is_active} onCheckedChange={(v) => set({ is_active: v })} />
          <span className="text-xs text-slate-600">{value.is_active ? "Active — can sign in" : "Disabled — cannot sign in"}</span>
        </div>
      )}

      {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <Button type="button" variant="outline" onClick={onCancel} className="border-slate-300">Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="user-submit-button">
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Send invitation"}
        </Button>
      </div>
    </form>
  );
}
